import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { API } from '../App';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { motion } from 'framer-motion';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { 
  Brain, 
  Clock, 
  Target, 
  TrendingUp, 
  AlertCircle, 
  CheckCircle2, 
  ArrowLeft, 
  Sparkles,
  BookOpen,
  Zap,
  Users,
  MessageCircle,
  Award,
  Star,
  BarChart3
} from 'lucide-react';
import { toast } from 'sonner';

const TestAnalysis = () => {
  const { testId } = useParams();
  const navigate = useNavigate();
  const [test, setTest] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [analysis, setAnalysis] = useState(null);
  const [overallAnalysis, setOverallAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [recommendations, setRecommendations] = useState([]);
  const [showCounselorModal, setShowCounselorModal] = useState(false);
  const [settings, setSettings] = useState(null);

  const safeSubjectAnalysis = analysis?.subject_analysis && typeof analysis.subject_analysis === 'object' ? analysis.subject_analysis : {};
  const safeChapterAnalysis = analysis?.chapter_analysis && typeof analysis.chapter_analysis === 'object' ? analysis.chapter_analysis : {};
  const safeTopicAnalysis = analysis?.topic_analysis && typeof analysis.topic_analysis === 'object' ? analysis.topic_analysis : {};

  useEffect(() => {
    fetchTestData();
    fetchOverallAnalysis();
    fetchSettings();
  }, [testId]);

  const fetchTestData = async () => {
    try {
      const [testRes, questionsRes] = await Promise.all([
        axios.get(`${API}/tests/${testId}`),
        axios.get(`${API}/tests/${testId}/questions`)
      ]);
      setTest(testRes.data);
      setQuestions(questionsRes.data);

      try {
        const analysisRes = await axios.get(`${API}/tests/${testId}/analysis`);
        setAnalysis(analysisRes.data);
      } catch (error) {
        console.info('Analysis not generated yet for this test');
      }
    } catch (error) {
      toast.error('Failed to load test data');
    } finally {
      setLoading(false);
    }
  };

  const fetchOverallAnalysis = async () => {
    try {
      const response = await axios.get(`${API}/overall-analysis`);
      setOverallAnalysis(response.data);
    } catch (error) {
      console.info('Overall analysis not generated yet');
    }
  };

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
    } catch (error) {
      console.warn('Settings load failed (non-critical):', error?.message);
    }
  };

  const generateAnalysis = async () => {
    setAnalyzing(true);
    try {
      const response = await axios.post(`${API}/tests/${testId}/analyze`);
      setAnalysis(response.data);
      toast.success('Analysis generated successfully!');
      
      // Get weak subjects for teacher recommendations
      await fetchTeacherRecommendations();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate analysis');
    } finally {
      setAnalyzing(false);
    }
  };

  const fetchTeacherRecommendations = useCallback(async () => {
    try {
      // Get unique subjects from wrong answers
      const wrongQuestions = questions.filter(q => q.answer_status === 'Wrong');
      const testWeakSubjects = [...new Set(wrongQuestions.map(q => q.subject))];
      const weakSubjects = overallAnalysis?.weak_subjects?.length
        ? overallAnalysis.weak_subjects
        : testWeakSubjects;
      
      if (weakSubjects.length > 0) {
        // Fetch top teachers for weak subjects
        const teacherPromises = weakSubjects.map(subject =>
          axios.get(`${API}/dashboard/recommendations?subject=${subject}`)
        );
        const teacherResponses = await Promise.all(teacherPromises);
        
        // Combine and deduplicate teachers
        const allTeachers = teacherResponses.flatMap(res => res.data);
        const uniqueTeachers = Array.from(
          new Map(allTeachers.map(t => [t.id, t])).values()
        ).slice(0, 3);
        
        setRecommendations(uniqueTeachers);
      }
    } catch (error) {
      console.warn('Teacher recommendations unavailable:', error?.message);
    }
  }, [questions, overallAnalysis]);

  useEffect(() => {
    if (questions.length > 0) {
      fetchTeacherRecommendations();
    }
  }, [questions.length, fetchTeacherRecommendations]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-lg text-slate-600">Loading test data...</div>
      </div>
    );
  }

  const getDifficultyBadge = (difficulty) => {
    const colors = {
      Easy: 'bg-emerald-100 text-emerald-700 border-emerald-300',
      Medium: 'bg-amber-100 text-amber-700 border-amber-300',
      Hard: 'bg-red-100 text-red-700 border-red-300'
    };
    return colors[difficulty] || colors.Medium;
  };

  const getStatusBadge = (status) => {
    return status === 'Right' 
      ? 'bg-emerald-100 text-emerald-700 border-emerald-300'
      : 'bg-red-100 text-red-700 border-red-300';
  };

  // Calculate statistics
  const correctCount = questions.filter(q => q.answer_status === 'Right').length;
  const accuracy = questions.length > 0 ? ((correctCount / questions.length) * 100).toFixed(1) : 0;
  const totalTime = questions.reduce((sum, q) => sum + q.time_spent, 0);
  const avgTimePerQuestion = questions.length > 0 ? (totalTime / questions.length).toFixed(1) : 0;

  // Chart data
  const difficultyData = ['Easy', 'Medium', 'Hard'].map(diff => {
    const diffQuestions = questions.filter(q => q.difficulty_level === diff);
    const correct = diffQuestions.filter(q => q.answer_status === 'Right').length;
    return {
      name: diff,
      correct,
      wrong: diffQuestions.length - correct,
      total: diffQuestions.length
    };
  }).filter(d => d.total > 0);

  const mistakeData = questions
    .filter(q => q.mistake_type)
    .reduce((acc, q) => {
      const existing = acc.find(item => item.name === q.mistake_type);
      if (existing) {
        existing.value++;
      } else {
        acc.push({ name: q.mistake_type, value: 1 });
      }
      return acc;
    }, []);

  const COLORS = ['#7C3AED', '#FACC15', '#10B981', '#F59E0B', '#3B82F6', '#EF4444'];

  const weakSubjects = overallAnalysis?.weak_subjects?.length
    ? overallAnalysis.weak_subjects
    : [...new Set(questions.filter(q => q.answer_status === 'Wrong').map(q => q.subject))];

  const weakTopics = overallAnalysis?.weak_topics?.length
    ? overallAnalysis.weak_topics
    : [...new Set(questions.filter(q => q.answer_status === 'Wrong').map(q => q.section))].slice(0, 3);

  const sanitizeWhatsappNumber = (number = '') => number.replace(/[^0-9]/g, '');

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid="test-analysis-page">
      <div className="mb-6">
        <Button
          variant="ghost"
          onClick={() => navigate('/')}
          className="mb-4"
          data-testid="back-to-dashboard-button"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-2">{test?.test_name}</h1>
        <p className="text-lg text-slate-600">
          {new Date(test?.test_date).toLocaleDateString()} • {questions.length} questions
        </p>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <motion.div whileHover={{ scale: 1.02, y: -5 }}>
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-600">Accuracy</p>
                  <p className="text-3xl font-bold text-emerald-600">{accuracy}%</p>
                  <p className="text-xs text-slate-500">{correctCount}/{questions.length} correct</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                  <Target className="w-6 h-6 text-emerald-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div whileHover={{ scale: 1.02, y: -5 }}>
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-600">Total Time</p>
                  <p className="text-3xl font-bold text-amber-600">{Math.floor(totalTime / 60)}m</p>
                  <p className="text-xs text-slate-500">{totalTime} seconds</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                  <Clock className="w-6 h-6 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div whileHover={{ scale: 1.02, y: -5 }}>
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-600">Avg Time/Q</p>
                  <p className="text-3xl font-bold text-violet-600">{avgTimePerQuestion}s</p>
                  <p className="text-xs text-slate-500">Per question</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-violet-100 flex items-center justify-center">
                  <Zap className="w-6 h-6 text-violet-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.div whileHover={{ scale: 1.02, y: -5 }}>
          <Card className="border-0 shadow-lg">
            <CardContent className="p-6">
              <div className="flex items-start justify-between">
                <div className="space-y-2">
                  <p className="text-sm font-medium text-slate-600">Questions</p>
                  <p className="text-3xl font-bold text-indigo-600">{questions.length}</p>
                  <p className="text-xs text-slate-500">Total attempted</p>
                </div>
                <div className="w-12 h-12 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <BookOpen className="w-6 h-6 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>


      {!analysis && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="mb-8"
        >
          <Card className="border-0 shadow-xl bg-gradient-to-br from-violet-50 to-indigo-50" data-testid="generate-analysis-card">
            <CardContent className="p-8 text-center">
              <div className="w-16 h-16 rounded-full bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center mx-auto mb-4">
                <Brain className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-bold text-slate-900 mb-2">Generate AI Analysis</h2>
              <p className="text-slate-600 mb-6 max-w-2xl mx-auto leading-relaxed">
                Get personalized insights powered by AI. Understand your thinking patterns, identify weak areas, 
                and receive strategic recommendations to improve your exam performance.
              </p>
              <Button
                onClick={generateAnalysis}
                disabled={analyzing}
                className="bg-violet-600 hover:bg-violet-700 text-white rounded-full px-8 h-12 font-medium shadow-lg shadow-violet-500/25"
                data-testid="generate-analysis-button"
              >
                {analyzing ? (
                  <>
                    <Sparkles className="w-5 h-5 mr-2 animate-spin" />
                    Analyzing...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-5 h-5 mr-2" />
                    Generate Analysis
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Teacher Recommendations - Show when analysis exists or weak areas detected */}
      {(analysis || questions.filter(q => q.answer_status === 'Wrong').length > 0) && (
        <Card className="border-0 shadow-xl bg-gradient-to-br from-amber-50 to-orange-50 mb-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="w-6 h-6 text-amber-600" />
              Need Help with Weak Topics?
            </CardTitle>
            <CardDescription>
              Based on your test performance, you might benefit from personalized teaching sessions
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Weak Areas Summary */}
            <div className="bg-white rounded-xl p-4 border border-amber-200">
              <h3 className="font-semibold text-slate-900 mb-3">Your Weak Areas:</h3>
              <div className="flex flex-wrap gap-2" data-testid="weak-areas-list">
                {weakSubjects.map(subject => (
                  <Badge key={subject} className="bg-red-100 text-red-700 border-red-300">
                    {subject}
                  </Badge>
                ))}
                {weakTopics.map(topic => (
                  <Badge key={topic} className="bg-orange-100 text-orange-700 border-orange-300">
                    {topic}
                  </Badge>
                ))}
                {weakSubjects.length === 0 && weakTopics.length === 0 && (
                  <span className="text-sm text-slate-500">No weak areas detected yet.</span>
                )}
              </div>
            </div>

            <div className="bg-amber-100 border border-amber-300 rounded-xl p-4">
              <p className="text-amber-900 font-medium mb-2">
                📚 Improve faster with expert teaching sessions!
              </p>
              <p className="text-sm text-amber-800">
                Our expert teachers can help you master these concepts through personalized attention and targeted practice.
              </p>
            </div>

            {/* Recommended Teachers */}
            {recommendations.length > 0 && (
              <div className="space-y-4">
                <h3 className="font-semibold text-slate-900">Recommended Teachers for You:</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {recommendations.map((teacher) => (
                    <Card key={teacher.id} className="border-amber-200 hover:shadow-lg transition-all">
                      <CardContent className="p-4 space-y-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-violet-100 to-indigo-100 flex items-center justify-center">
                            <BookOpen className="w-6 h-6 text-violet-600" />
                          </div>
                          <div className="flex-1">
                            <h4 className="font-semibold text-slate-900">{teacher.name}</h4>
                            <div className="flex items-center gap-1">
                              <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                              <span className="text-xs font-medium">{teacher.rating.toFixed(1)}</span>
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap gap-1">
                          {teacher.subjects.slice(0, 2).map(subject => (
                            <Badge key={subject} className="text-xs bg-violet-100 text-violet-700 border-violet-300">
                              {subject}
                            </Badge>
                          ))}
                        </div>

                        <div className="flex items-center gap-2 text-xs text-slate-600">
                          <Award className="w-3 h-3" />
                          <span>{teacher.experience_years}y exp</span>
                          <span>•</span>
                          <span>₹{teacher.rate_per_hour}/hr</span>
                        </div>

                        <Link to={`/teacher/${teacher.id}`}>
                          <Button size="sm" className="w-full bg-violet-600 hover:bg-violet-700 rounded-full">
                            View Profile
                          </Button>
                        </Link>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 pt-4">
              <Link to="/teachers" className="flex-1">
                <Button className="w-full bg-violet-600 hover:bg-violet-700 text-white rounded-full h-12 font-medium shadow-lg shadow-violet-500/25">
                  <Users className="w-5 h-5 mr-2" />
                  Browse All Teachers
                </Button>
              </Link>
              
              <Button
                onClick={() => setShowCounselorModal(true)}
                variant="outline"
                className="flex-1 border-amber-300 hover:bg-amber-50 rounded-full h-12 font-medium"
                data-testid="talk-to-counselor-button"
              >
                <MessageCircle className="w-5 h-5 mr-2" />
                Talk to a Counselor
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Counselor Contact Modal */}
      <Dialog open={showCounselorModal} onOpenChange={setShowCounselorModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Talk to a Counselor</DialogTitle>
            <DialogDescription>
              Our counselor will help you choose the right teacher and study plan
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="bg-violet-50 border border-violet-200 rounded-xl p-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-full bg-violet-600 flex items-center justify-center">
                  <MessageCircle className="w-6 h-6 text-white" />
                </div>
                <div>
                  <h4 className="font-semibold text-slate-900" data-testid="counselor-name">
                    {settings?.counselor_name || 'Education Counselor'}
                  </h4>
                  <p className="text-sm text-slate-600" data-testid="counselor-availability">
                    {settings?.counselor_availability_slots?.length
                      ? settings.counselor_availability_slots.join(' • ')
                      : 'Availability not set'}
                  </p>
                </div>
              </div>
              
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-slate-700">
                  <MessageCircle className="w-4 h-4" />
                  <span className="text-sm" data-testid="counselor-whatsapp">
                    WhatsApp: <strong>{settings?.counselor_whatsapp || 'Not provided'}</strong>
                  </span>
                </div>
                <div className="text-sm text-slate-600">
                  Message us about:
                  <ul className="list-disc list-inside mt-2 space-y-1">
                    <li>Choosing the right teacher for your needs</li>
                    <li>Creating a personalized study plan</li>
                    <li>Understanding our teaching methodology</li>
                    <li>Scheduling and payment assistance</li>
                  </ul>
                </div>
              </div>
            </div>

            <div className="flex gap-3">
              {settings?.counselor_whatsapp ? (
                <Button
                  onClick={() => window.open(
                    `https://wa.me/${sanitizeWhatsappNumber(settings.counselor_whatsapp)}?text=${encodeURIComponent(
                      'Hi, I need guidance on choosing a teacher for my weak topics.'
                    )}`,
                    '_blank'
                  )}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded-full"
                  data-testid="counselor-whatsapp-button"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  Chat on WhatsApp
                </Button>
              ) : (
                <Button
                  className="flex-1 rounded-full"
                  variant="outline"
                  disabled
                  data-testid="counselor-whatsapp-disabled"
                >
                  WhatsApp contact not available
                </Button>
              )}
              <Button
                onClick={() => setShowCounselorModal(false)}
                variant="outline"
                className="flex-1 rounded-full"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Tabs defaultValue="overview" className="mb-8">
        <TabsList className="mb-6">
          <TabsTrigger value="overview" data-testid="overview-tab">Overview</TabsTrigger>
          <TabsTrigger value="questions" data-testid="questions-tab">Questions</TabsTrigger>
          {analysis && <TabsTrigger value="insights" data-testid="insights-tab">AI Insights</TabsTrigger>}
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {/* Difficulty Analysis */}
          {difficultyData.length > 0 && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Performance by Difficulty</CardTitle>
                <CardDescription>How you performed across different difficulty levels</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart data={difficultyData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="name" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Bar dataKey="correct" fill="#10B981" name="Correct" radius={[8, 8, 0, 0]} />
                    <Bar dataKey="wrong" fill="#EF4444" name="Wrong" radius={[8, 8, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}

          {/* Mistake Distribution */}
          {mistakeData.length > 0 && (
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle>Mistake Distribution</CardTitle>
                <CardDescription>Types of errors you made</CardDescription>
              </CardHeader>
              <CardContent>
                <ResponsiveContainer width="100%" height={300}>
                  <PieChart>
                    <Pie
                      data={mistakeData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                      outerRadius={100}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {mistakeData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                  </PieChart>
                </ResponsiveContainer>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="questions">
          <Card className="border-0 shadow-lg">
            <CardHeader>
              <CardTitle>Question-by-Question Breakdown</CardTitle>
              <CardDescription>Detailed view of all questions</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3" data-testid="questions-list">
                {questions.map((q, index) => (
                  <motion.div
                    key={q.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.03 }}
                    className="p-4 rounded-xl border border-slate-200 hover:border-violet-300 hover:shadow-md transition-all duration-300"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-lg ${q.answer_status === 'Right' ? 'bg-emerald-100' : 'bg-red-100'} flex items-center justify-center font-bold ${q.answer_status === 'Right' ? 'text-emerald-600' : 'text-red-600'}`}>
                          {q.question_number}
                        </div>
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <Badge className={getDifficultyBadge(q.difficulty_level)}>
                              {q.difficulty_level}
                            </Badge>
                            <Badge className={getStatusBadge(q.answer_status)}>
                              {q.answer_status}
                            </Badge>
                          </div>
                          <p className="text-sm text-slate-600">
                            {q.subject} • {q.section} • {q.chapter}
                          </p>
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="flex items-center gap-1 text-slate-600">
                          <Clock className="w-4 h-4" />
                          <span className="text-sm font-medium">{q.time_spent}s</span>
                        </div>
                      </div>
                    </div>
                    {q.mistake_type && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                        <div className="flex items-center gap-2 text-amber-700">
                          <AlertCircle className="w-4 h-4" />
                          <span className="text-sm font-medium">Mistake: {q.mistake_type}</span>
                        </div>
                      </div>
                    )}
                  </motion.div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {analysis && (
          <TabsContent value="insights" className="space-y-6">
            {(analysis.student_improvement_score !== null && analysis.student_improvement_score !== undefined) && (
              <Card className="border-0 shadow-xl bg-gradient-to-br from-emerald-50 to-teal-50">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                    Student Improvement Score (SIS)
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div className="text-4xl font-bold text-slate-900">{analysis.student_improvement_score}/100</div>
                  {analysis.biggest_learning_gap && (
                    <p className="text-slate-700"><strong>Biggest gap:</strong> {analysis.biggest_learning_gap}</p>
                  )}
                  {analysis.error_personality && (
                    <p className="text-slate-700"><strong>Error personality:</strong> {analysis.error_personality}</p>
                  )}
                  {analysis.top_topics_to_improve?.length > 0 && (
                    <p className="text-slate-700"><strong>Top topics:</strong> {analysis.top_topics_to_improve.join(', ')}</p>
                  )}
                </CardContent>
              </Card>
            )}
            {/* Overall Insights */}
            <Card className="border-0 shadow-xl bg-gradient-to-br from-violet-50 to-indigo-50">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Brain className="w-6 h-6 text-violet-600" />
                  Overall Insights
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-lg text-slate-700 leading-relaxed">{analysis.overall_insights}</p>
              </CardContent>
            </Card>

            {/* Chapter Analysis */}
            {Object.keys(safeChapterAnalysis).length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BookOpen className="w-6 h-6 text-amber-600" />
                    Chapter-wise Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(safeChapterAnalysis).map(([chapter, insights]) => (
                      <div key={chapter} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                        <h4 className="font-semibold text-slate-900 mb-2">{chapter}</h4>
                        <p className="text-slate-700">{insights}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {Object.keys(safeSubjectAnalysis).length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-6 h-6 text-emerald-600" />
                    Subject Analysis
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {Object.entries(safeSubjectAnalysis).map(([subject, insight]) => (
                      <div key={subject} className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                        <h4 className="font-semibold text-slate-900 mb-1">{subject}</h4>
                        <p className="text-slate-700 text-sm">{insight}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Recommendations */}
            {analysis.recommendations && analysis.recommendations.length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-6 h-6 text-emerald-600" />
                    Strategic Recommendations
                  </CardTitle>
                  <CardDescription>Actionable steps to improve your performance</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analysis.recommendations.map((rec, index) => (
                      <motion.div
                        key={`rec-${index}-${rec.slice(0, 20)}`}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: index * 0.1 }}
                        className="flex items-start gap-3 p-4 rounded-xl bg-emerald-50 border border-emerald-200"
                      >
                        <CheckCircle2 className="w-5 h-5 text-emerald-600 mt-0.5 flex-shrink-0" />
                        <p className="text-slate-700 leading-relaxed">{rec}</p>
                      </motion.div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {analysis.action_plan?.length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <CheckCircle2 className="w-6 h-6 text-emerald-600" />
                    Concrete Action Plan
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analysis.action_plan.map((step, index) => (
                      <div key={`action-${index}-${step.slice(0, 20)}`} className="p-4 rounded-xl bg-emerald-50 border border-emerald-200">
                        <p className="text-slate-700">{step}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {Array.isArray(analysis.question_diagnostics) && analysis.question_diagnostics.length > 0 && (
              <Card className="border-0 shadow-lg">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="w-6 h-6 text-amber-600" />
                    Question Diagnostics
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {analysis.question_diagnostics.map((item, index) => (
                      <div key={`diag-${item.question_number}-${index}`} className="p-4 rounded-xl bg-slate-50 border border-slate-200">
                        <p className="font-semibold text-slate-900">Q{item.question_number} · {item.subject} · {item.chapter}</p>
                        <p className="text-sm text-slate-600">{item.diagnostic}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};

export default TestAnalysis;
